import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CopyIcon, MailIcon, Redo2Icon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Popup } from "simplydo/react/ui/popup";
import type { UiColor } from "simplydo/react/ui/utils/colors";

import api from "api";
import IdeasImage from "src/images/ideas.png";
import { useAppSelector } from "store";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";
import { getRoleIcon } from "components/lib/Roles/roleIcons";

import type { ChoosableUser, EmailInviteeUser } from "../types";

const ExtraContent = styled(ModalContent)`
  &:empty {
    display: none !important;
  }
`;

const persistentTokenMessages = {
  idea: {
    title: "ideas.teammates.link.title",
    description: "ideas.teammates.link.description",
    warning: "ideas.teammates.link.warning",
  },
  group: {
    title: "groups.members.link.title",
    description: "groups.members.link.description",
    warning: "groups.members.link.warning",
  },
  ideaBusinessProfile: {
    title: "ideaBusinessProfiles.members.link.title",
    description: "ideaBusinessProfiles.members.link.description",
    warning: "ideaBusinessProfiles.members.link.warning",
  },
};
type AvailablePersistentTokenTypes = keyof typeof persistentTokenMessages;

const defaultEnabledFeatures: {
  search?: boolean;
  invite?: boolean;
  persistentToken?: boolean;
} = { search: true, invite: true, persistentToken: false };

export type ExtraContentFunctionProps = {
  unpickUser: (userId: string) => void;
  users: Array<ChoosableUser>;
};

type UserChooserProps = {
  forType?:
    | "assessor"
    | "idea"
    | "group"
    | "projectInvited"
    | "projectAssignee"
    | "challenge"
    | "ideaBusinessProfile";
  forId?: string;
  forEntity?: "invitation";

  enabledFeatures?: typeof defaultEnabledFeatures;
  single?: boolean;
  clearOnComplete?: boolean;
  selectedUsers?: ChoosableUser[];

  subtitle?: string;
  confirm?: string;
  isLoading?: boolean;
  trigger?: React.ReactElement<{ onClick?: () => void }>;
  beforeContent?:
    | React.ReactNode
    | ((extraProps: ExtraContentFunctionProps) => React.ReactNode);
  afterContent?:
    | React.ReactNode
    | ((extraProps: ExtraContentFunctionProps) => React.ReactNode);

  searchFunction: (
    search: string,
    success: (users: OpenAPI.Schemas["User"][]) => void,
    failure: () => void,
  ) => void;
  isOpen?: boolean;
  onComplete: (users: ChoosableUser[]) => void;
  onClose?: () => void;
};

const SearchInfoText = "Search for an existing user by name or email address";
const InviteInfoText = "Invite a new user using their email address";

const SearchPlaceholderText = "Enter a search term";
const InvitePlaceholderText = "Enter an email address to invite";

const UserChooser = ({
  selectedUsers = [],
  trigger,
  subtitle,
  searchFunction,
  confirm,
  isLoading,
  onComplete,
  forType,
  forId,
  enabledFeatures = defaultEnabledFeatures,
  isOpen,
  onClose,
  clearOnComplete,
  single,
  forEntity = "invitation",
  beforeContent,
  afterContent,
}: UserChooserProps) => {
  const { t } = useTranslation();
  const [potentialUsers, setPotentialUsers] = useState<
    OpenAPI.Schemas["User"][]
  >([]);
  const [potentialUserSearch, setPotentialUserSearch] = useState("");
  const [chosenUsers, setChosenUsers] =
    useState<ChoosableUser[]>(selectedUsers);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(isOpen ?? false);
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);

  /*
    Persistent invitation token management
  */
  const [persistentToken, setPersistentToken] = useState<
    OpenAPI.Schemas["PersistentToken"] | null
  >(null);
  const [persistentTokenLoading, setPersistentTokenLoading] = useState(false);
  const [persistentTokenError, setPersistentTokenError] = useState<string>("");
  const [persistentTokenCreating, setPersistentTokenCreating] = useState(false);
  const [persistentTokenInvalidating, setPersistentTokenInvalidating] =
    useState(false);
  const persistentTokenLinkRef = useRef(null);

  const persistentTokenMessageData = useMemo(() => {
    if (!forType || !(forType in persistentTokenMessages)) {
      return undefined;
    }
    return persistentTokenMessages[forType as AvailablePersistentTokenTypes];
  }, [forType]);

  if (!persistentTokenMessageData && enabledFeatures.persistentToken) {
    throw new Error(
      `Invalid forType ${forType} for persistent token usage. To enable persistent tokens a "title" and "message" have to be provided.`,
    );
  }

  const createPersistentToken = useCallback(() => {
    if (!forType || !forId) {
      throw new Error(
        "forType and forId are required for persistent token creation",
      );
    }
    setPersistentTokenCreating(true);
    setPersistentTokenError("");
    api.persistentTokens.create(
      { forType, forId, forEntity },
      (data: OpenAPI.POST<"/persistent-tokens">["response"]) => {
        setPersistentTokenCreating(false);
        setPersistentToken(data.persistentToken);
        setPersistentTokenError("");
      },
      (err) => {
        setPersistentTokenCreating(false);
        setPersistentTokenError(err.message);
      },
    );
  }, [forEntity, forId, forType]);

  const getPersistentToken = useCallback(() => {
    if (!forType || !forId) {
      throw new Error(
        "forType and forId are required for persistent token creation",
      );
    }
    setPersistentTokenLoading(true);
    setPersistentTokenError("");
    api.persistentTokens.get(
      forType,
      forId,
      forEntity,
      (
        data: OpenAPI.GET<`/persistent-tokens/{for_type}/{for_id}/{for_entity}`>["response"],
      ) => {
        setPersistentTokenLoading(false);
        if (data.exists) {
          setPersistentToken(data.persistentToken);
        } else {
          createPersistentToken();
        }
      },
      (err) => {
        setPersistentTokenLoading(false);
        setPersistentTokenError(err.message);
      },
    );
  }, [forType, forId, forEntity, createPersistentToken]);

  const invalidatePersistentToken = useCallback(() => {
    if (!persistentToken) {
      throw new Error(
        "persistentToken is required for persistent token invalidation",
      );
    }
    util
      .confirm(
        "Are you sure you want to regenerate this link?",
        "Anyone with the previous link will no longer be able to use it. Only the most recent link will work.",
      )
      .then(() => {
        setPersistentTokenInvalidating(true);
        setPersistentTokenError("");
        api.persistentTokens.invalidate(
          persistentToken._id,
          (
            data: OpenAPI.POST<"/persistent-tokens/{id}/invalidate">["response"],
          ) => {
            setPersistentTokenInvalidating(false);
            setPersistentToken(data.persistentToken);
            toast.success("New link generated");
          },
          (err) => {
            setPersistentTokenInvalidating(false);
            setPersistentTokenError(err.message);
          },
        );
      })
      .catch(() => {});
  }, [persistentToken]);

  useEffect(() => {
    if (open && enabledFeatures.persistentToken) {
      getPersistentToken();
    }
  }, [enabledFeatures.persistentToken, getPersistentToken, open]);

  const copyTokenLink = useCallback(() => {
    if (persistentTokenLinkRef.current) {
      void util.copyToClipboard(
        util.getBaseUrl(
          user?.ownerOrganisation,
          `/join/${persistentToken?.token}`,
        ),
      );
    }
  }, [user?.ownerOrganisation, persistentToken?.token]);

  const debouncedSearch = useThrottle(searchFunction, 400, [searchFunction]);

  useEffect(() => {
    if (!enabledFeatures.search) return;
    if (!open && !isOpen) {
      if (potentialUserSearch !== "") {
        setPotentialUserSearch("");
        setPotentialUsers([]);
      }
      return;
    }

    if (!potentialUserSearch) {
      setPotentialUsers([]);
    } else {
      setLoading(true);
      debouncedSearch(
        potentialUserSearch,
        (users) => {
          setPotentialUsers(users);
          setLoading(false);
        },
        () => {
          setPotentialUsers([]);
          setLoading(false);
        },
      );
    }
  }, [
    potentialUserSearch,
    debouncedSearch,
    isOpen,
    open,
    enabledFeatures.search,
  ]);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(!!isOpen);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (isOpen === undefined) {
      setOpen(false);
    }
    onClose?.();
  }, [onClose, isOpen]);

  const pickUser = useCallback(
    (u: ChoosableUser) => {
      if (single) {
        setChosenUsers([u]);
      } else {
        setChosenUsers((prev) => prev.filter((c) => c._id !== u._id).concat(u));
      }
    },
    [single],
  );

  const unpickUser = useCallback((userId: string) => {
    setChosenUsers((prev) => prev.filter((u) => u._id !== userId));
  }, []);

  const finish = () => {
    onComplete?.(chosenUsers);

    if (isOpen === undefined) {
      setOpen(false);
    }
    onClose?.();

    if (clearOnComplete) {
      setPotentialUserSearch("");
      setPotentialUsers([]);
      setChosenUsers([]);
    }
  };

  const inviteUser = useCallback(() => {
    // If search is not allowed we should always use the email directly rather than resolve the account
    if (enabledFeatures.search) {
      // check if a user with the email holds an account
      api.users.findByEmail(
        { email: potentialUserSearch, forType, forId },
        (data) => {
          if (data?.user) {
            pickUser(data.user);
          } else {
            // Validate email entered
            if (!util.validateEmail(potentialUserSearch)) {
              toast.error("Please enter a valid email address");
              return;
            }
            setChosenUsers([
              ...chosenUsers,
              {
                isEmailInvitee: true,
                _id: potentialUserSearch,
                profile: { fullName: potentialUserSearch },
                isInAudience: false,
              } satisfies EmailInviteeUser,
            ]);
          }
          setPotentialUserSearch("");
        },
        () => {},
      );
    } else {
      // Validate email entered
      if (!util.validateEmail(potentialUserSearch)) {
        toast.error("Please enter a valid email address");
        return;
      }
      setChosenUsers([
        ...chosenUsers,
        {
          isEmailInvitee: true,
          _id: potentialUserSearch,
          profile: { fullName: potentialUserSearch },
          isInAudience: false,
        } satisfies EmailInviteeUser,
      ]);
    }
  }, [
    enabledFeatures.search,
    potentialUserSearch,
    forType,
    forId,
    pickUser,
    chosenUsers,
  ]);

  const shouldShowEmailInvitationButton = useMemo(
    () =>
      (util.validateEmail(potentialUserSearch) &&
        !potentialUsers.length &&
        enabledFeatures.invite) ||
      (enabledFeatures.invite && !enabledFeatures.search),
    [
      enabledFeatures.invite,
      enabledFeatures.search,
      potentialUserSearch,
      potentialUsers.length,
    ],
  );

  const chosenIds = useMemo(() => chosenUsers.map((c) => c._id), [chosenUsers]);
  const potentialIds = useMemo(
    () => potentialUsers.map((c) => c._id),
    [potentialUsers],
  );
  const filteredChosenUsers = useMemo(
    () => chosenUsers.filter((p) => !potentialIds.includes(p._id)),
    [potentialIds, chosenUsers],
  );

  const beforeContentResolved = useMemo(() => {
    if (typeof beforeContent === "function") {
      return beforeContent({ users: chosenUsers, unpickUser: unpickUser });
    }
    return beforeContent;
  }, [beforeContent, chosenUsers, unpickUser]);

  const afterContentResolved = useMemo(() => {
    if (typeof afterContent === "function") {
      return afterContent({ users: chosenUsers, unpickUser: unpickUser });
    }
    return afterContent;
  }, [afterContent, chosenUsers, unpickUser]);

  return (
    <>
      {trigger
        ? isOpen === undefined
          ? React.cloneElement(trigger, { onClick: () => setOpen(!open) })
          : trigger
        : null}

      <Modal
        open={open}
        size={enabledFeatures.persistentToken ? "large" : "small"}
        onClose={handleClose}
        data-testid="simplydo-user-chooser"
        className={theme.sizes.isComputer ? "no-scroll" : undefined}
      >
        <ModalHeader>
          {enabledFeatures.invite
            ? t("userchooser.search.external")
            : t("userchooser.search.internal")}
          {subtitle && (
            <p style={{ fontSize: "1rem", fontWeight: "normal" }}>{subtitle}</p>
          )}
        </ModalHeader>
        {beforeContentResolved ? (
          <ExtraContent>{beforeContentResolved}</ExtraContent>
        ) : null}
        <ModalContent scrolling>
          <Grid
            columns={
              enabledFeatures.persistentToken && !theme.sizes.isMobile ? 2 : 1
            }
          >
            {enabledFeatures.persistentToken ? (
              <GridColumn
                stretched
                style={{ marginBottom: theme.sizes.isMobile ? 20 : undefined }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {persistentTokenMessageData ? (
                    <span>
                      <b>{t(persistentTokenMessageData.title)}</b>
                      <p>{t(persistentTokenMessageData.description)}</p>
                    </span>
                  ) : null}

                  {persistentTokenMessageData?.warning ? (
                    <Message color="orange" style={{ margin: 0 }}>
                      {t(persistentTokenMessageData.warning)}
                    </Message>
                  ) : null}

                  {persistentTokenLoading || persistentTokenCreating ? (
                    <Loader />
                  ) : null}

                  {persistentTokenError ? (
                    <Message color="red">
                      <MessageHeader>Error</MessageHeader>
                      <p>{persistentTokenError}</p>
                    </Message>
                  ) : null}

                  {persistentToken ? (
                    <>
                      <Input
                        ref={persistentTokenLinkRef}
                        loading={persistentTokenInvalidating}
                        fluid
                        value={util.getBaseUrl(
                          user?.ownerOrganisation,
                          `/join/${persistentToken.token}`,
                        )}
                        readOnly
                        action={
                          <Button
                            data-tooltip="Copy link"
                            onClick={copyTokenLink}
                            disabled={persistentTokenInvalidating}
                          >
                            <Icon icon={CopyIcon} />
                          </Button>
                        }
                      />
                      <Popup
                        trigger={
                          <Button
                            fluid
                            onClick={invalidatePersistentToken}
                            loading={persistentTokenInvalidating}
                            disabled={persistentTokenInvalidating}
                          >
                            <Icon icon={Redo2Icon} />
                            Regenerate link
                          </Button>
                        }
                      >
                        Regenerate this link to invalidate the previous one,
                        meaning it can no longer be used
                      </Popup>{" "}
                    </>
                  ) : null}
                </div>
              </GridColumn>
            ) : null}
            <GridColumn>
              <p style={{ marginBottom: 8 }}>
                <b>Find and select {single ? "a user" : "users"}</b>
                <p>
                  {enabledFeatures.search && enabledFeatures.invite
                    ? `${SearchInfoText}, or ${InviteInfoText.toLowerCase()}`
                    : enabledFeatures.search
                      ? SearchInfoText
                      : InviteInfoText}
                  .
                </p>
              </p>
              <Input
                loading={loading}
                placeholder={
                  enabledFeatures.search && enabledFeatures.invite
                    ? `${SearchPlaceholderText}, or ${InvitePlaceholderText.toLowerCase()}`
                    : enabledFeatures.search
                      ? SearchPlaceholderText
                      : InvitePlaceholderText
                }
                fluid
                value={potentialUserSearch}
                onChange={(e) => setPotentialUserSearch(e.target.value)}
                action={
                  shouldShowEmailInvitationButton ? (
                    <Button onClick={inviteUser} color="secondary">
                      Add invitation
                    </Button>
                  ) : null
                }
                style={{ marginBottom: 5 }}
              />
              {!chosenUsers?.length && !potentialUsers?.length ? (
                <Card fluid placeholder elevation="small">
                  <CardContent style={{ textAlign: "center" }}>
                    <img
                      src={IdeasImage}
                      style={{
                        maxHeight: 100,
                        maxWidth: "80%",
                        display: "block",
                        margin: "5px auto",
                      }}
                      alt="No users chosen yet"
                    />
                    {potentialUserSearch && !loading ? (
                      <>
                        <H4 style={{ marginBottom: 4 }}>
                          {t("userchooser.search.none.found")}
                        </H4>
                        <span>{t("userchooser.search.info")}</span>
                      </>
                    ) : (
                      <>
                        <H4 style={{ marginBottom: 4 }}>
                          {t("userchooser.search.none.selected")}
                        </H4>
                        <span>{t("userchooser.search.none.info")}</span>
                      </>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <ConfigurableTable
                  preventSelectAll={single}
                  selectedKeys={chosenIds}
                  tableKey="userChooser"
                  data={filteredChosenUsers.concat(potentialUsers)}
                  onSelect={(item, selected) => {
                    if (!item) {
                      if (selected) {
                        setChosenUsers(
                          filteredChosenUsers.concat(potentialUsers),
                        );
                      } else {
                        setChosenUsers([]);
                      }
                      return;
                    }
                    if (selected) {
                      pickUser(item);
                    } else {
                      unpickUser(item._id);
                    }
                  }}
                  columns={[
                    {
                      key: "fullName",
                      name: "Name",
                      render: ({ item }) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {item.isEmailInvitee ? (
                            <Icon icon={MailIcon} size="large" color="grey" />
                          ) : (
                            <ImageWithFallback
                              style={{ objectFit: "cover" }}
                              avatar
                              src={util.avatarUrl(
                                "iconRoles" in item ? item : undefined,
                              )}
                              fallbackSrc={util.avatarUrl()}
                            />
                          )}
                          {item.profile?.fullName ?? ""}
                        </div>
                      ),
                    },
                    {
                      key: "role",
                      name: "Role",
                      render: ({ item }) =>
                        "iconRoles" in item && item.iconRoles?.length ? (
                          <Popup
                            trigger={
                              <Icon
                                icon={getRoleIcon(
                                  item.iconRoles[0]?.icon?.name,
                                )}
                                color={
                                  item.iconRoles[0]?.icon?.colour as UiColor
                                }
                                style={{ marginLeft: 5 }}
                              />
                            }
                          >
                            {item.iconRoles[0]?.name}
                          </Popup>
                        ) : null,
                    },
                    {
                      key: "jobTitle",
                      name: "Job Title",
                      render: ({ item }) =>
                        item.profile && "jobTitle" in item.profile
                          ? item.profile.jobTitle
                          : undefined,
                    },
                    {
                      key: "department",
                      name: "Department",
                      render: ({ item }) =>
                        item.profile && "department" in item.profile
                          ? item.profile.department
                          : undefined,
                    },
                  ]}
                />
              )}
            </GridColumn>
          </Grid>
        </ModalContent>
        {afterContentResolved ? (
          <ExtraContent>{afterContentResolved}</ExtraContent>
        ) : null}
        <ModalActions>
          <Button onClick={() => handleClose()}>{t("generic.cancel")}</Button>
          <Button
            disabled={chosenUsers?.length === 0}
            onClick={finish}
            loading={isLoading}
            color="primary"
          >
            {confirm ||
              `Continue with ${util.pluralise(chosenUsers.length, "user", "users")}`}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
};

export default UserChooser;
